import { generateIcsCalendar, IcsEvent } from 'ts-ics'
import { computed, Ref } from 'vue'

import { getDefaultCalendar, toScheduleOrganizerIdentity } from '@/entities/schedule'

import { useCurrentUser } from '@/shared/composables/current-user'

export interface DownloadIcsOptions {
  summary?: string
  description?: string
}

export const useScheduleDownloadIcs = (eventRef: Ref<IcsEvent | undefined>) => {
  const { currentUser } = useCurrentUser()

  const hasEvent = computed(() => {
    return !!eventRef.value
  })

  const getFile = (event?: IcsEvent): File => {
    const eventToUse = event || eventRef.value
    if (!eventToUse) {
      throw new Error('No file')
    }
    const fileName = `${eventToUse.start.date.getTime()}.ics`
    const identity = toScheduleOrganizerIdentity(currentUser.value)
    const content = generateIcsCalendar(getDefaultCalendar(eventToUse, {
      includeSequence: true,
      ...(identity ? { identity } : {}),
    }))
    const file: File = new File([content], fileName, { type: 'text/calendar' })
    return file
  }

  const downloadIcs = (options?: DownloadIcsOptions) => {
    if (!eventRef.value) {
      return
    }

    let eventToDownload = eventRef.value

    if (options) {
      eventToDownload = {
        ...eventRef.value,
        ...(options.summary !== undefined && { summary: options.summary }),
        ...(options.description !== undefined && { description: options.description }),
      }
    }

    const file = getFile(eventToDownload)
    const url = URL.createObjectURL(file)

    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    URL.revokeObjectURL(url)
  }

  return {
    hasEvent,
    downloadIcs,
    getFile,
  }
}
